import i18n from "@/plugins/i18n";
import { HyperlinkGetDto, HyperlinkSourceTypeDto, HyperlinkSourceGetDto } from "./types";
import { getSourceObjectNamesAsync, getSourceIdentifier } from "./objecttypes";

export { HyperlinkGetDto } from "./types";

type HyperlinkSourceWithLink = { source: HyperlinkSourceGetDto; hyperlinkId: string; description?: string };

export async function getHyperlinkSourceDescriptions(links: HyperlinkGetDto[]): Promise<Map<string, string>> {
    // Get unique source items
    const linksToFetch = links
        .reduce((acc, link) => {
            //compose a key of type+source-identifier to identify source entities
            const linkSourceKey = `${link.source.type}__${getSourceIdentifier({ source: link.source, hyperlinkId: link.hyperlink_id })}`;
            if (!acc.has(linkSourceKey)) {
                acc.set(linkSourceKey, {
                    source: link.source,
                    hyperlinkId: link.hyperlink_id
                });
            }
            return acc;
        }, new Map<string, HyperlinkSourceWithLink>())
        .values(); // dont need the keys those were just to uniqify the sources

    // Group sources per type
    const groupedByType = Array.from(linksToFetch).reduce((acc, link) => {
        if (!acc.has(link.source.type)) {
            acc.set(link.source.type, []);
        }
        acc.get(link.source.type).push(link);
        return acc;
    }, new Map<HyperlinkSourceTypeDto, HyperlinkSourceWithLink[]>());

    // iterate entries to fetch titles, returns promise for each group
    const promisesToAwait: Promise<void>[] = Array.from(groupedByType.entries()).map(
        async ([type, links]): Promise<void> => {
            const fetchedNames = await getSourceObjectNamesAsync(type, links);
            links.forEach(link => {
                //set title on each link
                const title = fetchedNames.get(link.hyperlinkId);
                if (title) {
                    const languageKey = `hyperlink.objectTypeDescription.${type}`;
                    link.description = i18n.t(languageKey, { name: title }) as string;
                }
            });

            // if there are items not described group them to a single item with a count and type name
            const describedLinks = links.filter(link => link.description);
            if (describedLinks.length < links.length) {
                const undescribedDocumentsCount = links.length - describedLinks.length;
                const undescribedDocumentsItem = getUndescribedEntitiesItem(describedLinks.length, undescribedDocumentsCount, type);
                //replace the links in the list with the described links and a single item for all undescribed items
                links.splice(0, links.length, ...describedLinks, undescribedDocumentsItem);
            }
        }
    );

    //wait for all the fetches to complete
    await Promise.allSettled(promisesToAwait);

    // create a map descriptions per link
    const sourceTitles: Map<string, string> = new Map(
        Array.from(groupedByType.values()).flatMap(links => links.map(link => [link.hyperlinkId, link.description]))
    );
    return sourceTitles;
}

function getUndescribedEntitiesItem(describedCount: number, count: number, entityType: HyperlinkSourceTypeDto): HyperlinkSourceWithLink {
    const languageKey = "hyperlink.objectTypeDescription." + (describedCount > 0 ? "otherEntityCount" : "entityCount");
    return {
        description: i18n.tc(languageKey, count, {
            count: count,
            entityPluralized: i18n.tc(`hyperlink.entityName.${entityType}`, count)
        }) as string,
        source: null,
        hyperlinkId: entityType + "_undescribed"
    };
}
